import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import Icon from '@/components/AppIcon';
import { PageLayout } from '@/components/ui/PageLayout';
import { Switch } from '@/components/ui/Switch';
import { Spinner } from '@/components/ui/Spinner';
import { Laptop } from 'lucide-react';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import {
  getAssetSettings,
  updateAssetSettings,
} from '../../../../api/General/assets';
import AssetCategoriesTab from '../../../General/Assets/components/AssetCategoriesTab';
import AssetRolesTab from '../../../General/Assets/components/AssetRolesTab';
import AssetLocationsTab from '../../../General/Assets/components/AssetLocationsTab';
import ReplenishmentTab from '../../../General/Assets/components/ReplenishmentTab';
import ReturnReasonsTab from '../../../General/Assets/components/ReturnReasonsTab';
import { useCanManageAssets, useCanAdministerAssetRoles } from '@/hooks/useCanManageAssets';

type Tab = 'otp' | 'categories' | 'assetRoles' | 'locations' | 'returnReasons' | 'replenishment';

const AssetManagementSettings: React.FC = () => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  // Config tabs: effective asset-manager capability (AssetManagement tag OR Hr).
  const isAssetManager = useCanManageAssets();
  // Granting/revoking the tag is a separate concern from holding it: Hr AND Admin.
  const canAdministerAssetRoles = useCanAdministerAssetRoles();

  const [activeTab, setActiveTab] = useState<Tab>(isAssetManager ? 'otp' : 'assetRoles');
  // The capability can arrive after mount (GetUserInfo reconcile), so re-seed the tab when
  // it flips on rather than leaving the user parked on the roles tab.
  const sawAssetManagerRef = React.useRef(isAssetManager);
  useEffect(() => {
    if (isAssetManager && !sawAssetManagerRef.current) {
      sawAssetManagerRef.current = true;
      setActiveTab('otp');
    }
  }, [isAssetManager]);
  const [requireOtp, setRequireOtp] = useState(true);
  const [loading, setLoading] = useState(isAssetManager);
  const [settingsError, setSettingsError] = useState(false);
  const [saving, setSaving] = useState(false);

  // GetAssetSettings is Policy="AssetManagement" — an Admin without the tag is routed here
  // (to administer the tag) but would get a guaranteed 403. Only fetch when the user
  // actually holds the asset capability, and surface a failure instead of silently
  // leaving `requireOtp` on its optimistic default.
  useEffect(() => {
    if (!isAssetManager) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      setSettingsError(false);
      try {
        const r = await getAssetSettings();
        if (r.errors.length === 0) {
          setRequireOtp(r.payload.requireAssetIssuanceOtp);
        } else {
          setSettingsError(true);
        }
      } catch {
        setSettingsError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAssetManager]);

  const onToggle = async (next: boolean) => {
    const prev = requireOtp;
    setRequireOtp(next);
    setSaving(true);
    try {
      const r = await updateAssetSettings({ requireAssetIssuanceOtp: next });
      if (r.errors.length !== 0) {
        setRequireOtp(prev);
        showToast(r.errors[0]?.description ?? t('assetSettings.updateFailed'), 'error');
      } else {
        showToast(t('assetSettings.saved'), 'success');
      }
    } catch {
      setRequireOtp(prev);
      showToast(t('assetSettings.updateFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title={t('assetSettings.title')}
      description={t('assetSettings.otpExplain')}
      icon={Laptop}
      accentColor="amber"
    >
      {!isAssetManager && !canAdministerAssetRoles ? (
        <div className="rounded-lg border border-border bg-card p-6" data-testid="asset-settings-no-access">
          <p className="text-sm text-muted-foreground">{t('assetSettings.noAccess')}</p>
        </div>
      ) : (
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
        <TabsList className="mb-6">
          {isAssetManager && (
            <TabsTrigger value="otp" className="flex items-center gap-2">
              <Icon name="ShieldCheck" size={16} />
              {t('assetSettings.otpTab')}
            </TabsTrigger>
          )}
          {isAssetManager && (
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Icon name="Tag" size={16} />
              {t('assetSettings.categoriesTab')}
            </TabsTrigger>
          )}
          {isAssetManager && (
            <TabsTrigger value="locations" className="flex items-center gap-2">
              <Icon name="MapPin" size={16} />
              {t('assetSettings.locationsTab')}
            </TabsTrigger>
          )}
          {isAssetManager && (
            <TabsTrigger value="returnReasons" className="flex items-center gap-2">
              <Icon name="Undo2" size={16} />
              {t('assetSettings.returnReasonsTab')}
            </TabsTrigger>
          )}
          {isAssetManager && (
            <TabsTrigger value="replenishment" className="flex items-center gap-2">
              <Icon name="RefreshCw" size={16} />
              {t('assetSettings.replenishmentTab')}
            </TabsTrigger>
          )}
          {canAdministerAssetRoles && (
            <TabsTrigger value="assetRoles" className="flex items-center gap-2">
              <Icon name="UserCog" size={16} />
              {t('assetSettings.assetRolesTab')}
            </TabsTrigger>
          )}
        </TabsList>

        {isAssetManager && (
          <TabsContent value="otp">
            {loading ? (
              <div className="flex justify-center p-8">
                <Spinner size="lg" />
              </div>
            ) : settingsError ? (
              <div className="rounded-lg border border-border bg-card p-6" data-testid="asset-settings-error">
                <p className="text-sm text-destructive">{t('assetSettings.loadError')}</p>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-start gap-4">
                  <Switch
                    checked={requireOtp}
                    onCheckedChange={onToggle}
                    disabled={saving}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-base font-semibold text-foreground">
                      {t('assetSettings.requireOtpLabel')}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t('assetSettings.otpExplain')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        )}

        {isAssetManager && (
          <TabsContent value="categories">
            <AssetCategoriesTab />
          </TabsContent>
        )}

        {isAssetManager && (
          <TabsContent value="locations">
            <AssetLocationsTab />
          </TabsContent>
        )}

        {isAssetManager && (
          <TabsContent value="returnReasons">
            <ReturnReasonsTab />
          </TabsContent>
        )}

        {isAssetManager && (
          <TabsContent value="replenishment">
            <ReplenishmentTab />
          </TabsContent>
        )}

        {canAdministerAssetRoles && (
          <TabsContent value="assetRoles">
            <AssetRolesTab />
          </TabsContent>
        )}
      </Tabs>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default AssetManagementSettings;
